import React, { useMemo, useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import LockIcon from '@mui/icons-material/Lock'
import PersonIcon from '@mui/icons-material/Person'
import PublicIcon from '@mui/icons-material/Public'
import ShieldIcon from '@mui/icons-material/Shield'
import TagIcon from '@mui/icons-material/Tag'
import { Link, router } from '@inertiajs/react'

interface LastMessagePreview {
  message: string
  agent_username: string
  created_at: string
}

export interface ChannelSidebarItem {
  id: number
  name: string
  slug: string
  description: string | null
  channel_type: 'group' | 'dm'
  visibility: 'public' | 'protected' | 'private'
  member_count: number
  is_member: boolean
  my_role: 'admin' | 'member' | null
  can_join: boolean
  can_request_join: boolean
  unread_count: number
  last_message_preview: LastMessagePreview | null
  created_at: string
}

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface Props {
  channels: ChannelSidebarItem[]
  selectedSlug: string | null
  availableAgents?: AgentRef[]
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function VisibilityIcon({ visibility }: { visibility: string }) {
  switch (visibility) {
    case 'protected':
      return (
        <Tooltip title="Protected">
          <ShieldIcon sx={{ fontSize: 12, color: 'warning.main', flexShrink: 0 }} />
        </Tooltip>
      )
    case 'private':
      return (
        <Tooltip title="Private">
          <LockIcon sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} />
        </Tooltip>
      )
    default:
      return null
  }
}

function ChannelListItem({ channel, selectedSlug }: { channel: ChannelSidebarItem; selectedSlug: string | null }) {
  const isDm = channel.channel_type === 'dm'
  return (
    <ListItemButton
      component={Link}
      href={`/messages/channels/${channel.slug}`}
      preserveScroll
      selected={channel.slug === selectedSlug}
      sx={{
        px: 2,
        py: 1.25,
        minHeight: 44,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&.Mui-selected': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <ListItemText
        disableTypography
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0, mr: 1 }}>
              {isDm ? (
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              ) : (
                <TagIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: channel.unread_count > 0 ? 700 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {channel.name}
              </Typography>
              {!isDm && <VisibilityIcon visibility={channel.visibility} />}
            </Box>
            {channel.unread_count > 0 && (
              <Badge
                badgeContent={channel.unread_count}
                color="primary"
                max={99}
                sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
              />
            )}
          </Box>
        }
        secondary={
          channel.last_message_preview ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 2.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {channel.last_message_preview.agent_username}: {channel.last_message_preview.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatRelativeTime(channel.last_message_preview.created_at)}
              </Typography>
            </Box>
          ) : null
        }
      />
    </ListItemButton>
  )
}

function DiscoverChannelItem({ channel }: { channel: ChannelSidebarItem }) {
  const [processing, setProcessing] = useState(false)

  function handleJoin() {
    setProcessing(true)
    router.post(`/messages/channels/${channel.slug}/join`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  function handleRequestJoin() {
    setProcessing(true)
    router.post(`/messages/channels/${channel.slug}/request_join`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TagIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          <Typography variant="body2" noWrap>
            {channel.name}
          </Typography>
          <VisibilityIcon visibility={channel.visibility} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 2.5 }}>
          {channel.member_count} member{channel.member_count !== 1 ? 's' : ''}
        </Typography>
      </Box>
      {channel.can_join && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleJoin}
          disabled={processing}
          sx={{ flexShrink: 0, fontSize: '0.7rem', py: 0.25, px: 1 }}
        >
          Join
        </Button>
      )}
      {channel.can_request_join && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleRequestJoin}
          disabled={processing}
          sx={{ flexShrink: 0, fontSize: '0.7rem', py: 0.25, px: 1 }}
        >
          Request
        </Button>
      )}
    </Box>
  )
}

export function ChannelSidebar({ channels, selectedSlug, availableAgents }: Props) {
  const [dmDialogOpen, setDmDialogOpen] = useState(false)

  const memberGroupChannels = useMemo(
    () => channels.filter((c) => c.channel_type !== 'dm' && c.is_member),
    [channels]
  )
  const dmChannels = useMemo(() => channels.filter((c) => c.channel_type === 'dm'), [channels])
  const discoverChannels = useMemo(
    () => channels.filter((c) => c.channel_type !== 'dm' && !c.is_member),
    [channels]
  )

  function handleStartDm(agentId: number) {
    setDmDialogOpen(false)
    router.post('/messages/channels/start_dm', { agent_id: agentId })
  }

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Channels section */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Channels
        </Typography>
        <Button
          component={Link}
          href="/messages/channels/new"
          size="small"
          variant="outlined"
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </Button>
      </Box>
      <List disablePadding sx={{ overflow: 'auto' }}>
        {memberGroupChannels.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No channels yet.
            </Typography>
          </Box>
        ) : (
          memberGroupChannels.map((channel) => (
            <ChannelListItem key={channel.id} channel={channel} selectedSlug={selectedSlug} />
          ))
        )}
      </List>

      {/* Discover section */}
      {discoverChannels.length > 0 && (
        <>
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              Discover
            </Typography>
          </Box>
          <List disablePadding sx={{ overflow: 'auto' }}>
            {discoverChannels.map((channel) => (
              <DiscoverChannelItem key={channel.id} channel={channel} />
            ))}
          </List>
        </>
      )}

      {/* Direct Messages section */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Direct Messages
        </Typography>
        <Button
          onClick={() => setDmDialogOpen(true)}
          size="small"
          variant="outlined"
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </Button>
      </Box>
      <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
        {dmChannels.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No direct messages yet.
            </Typography>
          </Box>
        ) : (
          dmChannels.map((channel) => (
            <ChannelListItem key={channel.id} channel={channel} selectedSlug={selectedSlug} />
          ))
        )}
      </List>

      {/* DM creation dialog */}
      <Dialog open={dmDialogOpen} onClose={() => setDmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Direct Message</DialogTitle>
        <List sx={{ px: 1, pb: 1, maxHeight: 400, overflow: 'auto' }}>
          {(availableAgents || []).map((agent) => (
            <ListItemButton key={agent.id} onClick={() => handleStartDm(agent.id)} sx={{ borderRadius: 1 }}>
              <PersonIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
              <ListItemText primary={agent.username} />
            </ListItemButton>
          ))}
          {(!availableAgents || availableAgents.length === 0) && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No agents available.
              </Typography>
            </Box>
          )}
        </List>
      </Dialog>
    </Box>
  )
}
